import {
  COMMON_PATHS,
  findLayersOrLayerAssets,
  getPath,
} from "@blings/blings-player";
import { getLayersWithColorArray } from "../components/mods/modForms/ColorModForms/ColorModsHelpers";

type AdditionalLayer = {
  assetId?: string;
  layerName?: string;
  layerUid?: number;
};

type FilterArgs = {
  layerName: string;
  assetId?: string;
  layerUid?: number;
  additionals?: AdditionalLayer[];
  json: any;
  fallbackAssetId?: string;
};

export type LayerSelectorOption = {
  renderText: string;
  layerName: string;
  layerUid?: number;
  parent?: string;
  parentLabel?: string;
  child?: string;
  assetId?: string;
};

const MAIN_ASSET = "main";

const normalizeAssetId = (
  assetId: string | undefined,
  fallbackAssetId: string
): string => (assetId && assetId.length ? assetId : fallbackAssetId);

const findLayerEntries = ({
  assetId,
  layerName,
  layerUid,
  additionals,
  json,
  fallbackAssetId,
}: FilterArgs) => {
  // const normalizedAssetId = normalizeAssetId(assetId, fallbackAssetId || nu);
  return findLayersOrLayerAssets({
    assetId: assetId === MAIN_ASSET ? undefined : assetId,
    layerName,
    jsonVid: json,
    layerUid,
    additionals,
  });
};

export const resolveAssetLabel = (assetId: string, json: any): string => {
  if (assetId === MAIN_ASSET) return MAIN_ASSET;
  const assets = Array.isArray(json?.assets) ? json.assets : [];
  const assetById = assets.find((asset) => asset?.id === assetId);
  if (assetById?.nm) return assetById.nm;
  return assetId;
};

const buildParentChild = (
  assetId: string,
  layerName: string,
  assetLabel: string
): Pick<LayerSelectorOption, "parent" | "parentLabel" | "child"> => {
  if (assetId === MAIN_ASSET) {
    return { parent: layerName, child: undefined };
  }
  return { parent: assetId, parentLabel: assetLabel, child: layerName };
};

const buildRenderText = (
  label: string,
  layerName: string,
  assetId: string
): string => {
  if (label === layerName) {
    return `${label} - ${assetId}`;
  }
  return `${label} [${layerName} - ${assetId}]`;
};

const buildBaseOption = (
  args: FilterArgs,
  renderText: string
): LayerSelectorOption => {
  const fallbackAssetId = args.fallbackAssetId ?? MAIN_ASSET;
  const assetId = normalizeAssetId(args.assetId, fallbackAssetId);
  const assetLabel = resolveAssetLabel(assetId, args.json);
  return {
    renderText,
    layerName: args.layerName,
    layerUid: args.layerUid,
    assetId,
    ...buildParentChild(assetId, args.layerName, assetLabel),
  };
};

export const buildTextLayerOption = (
  args: FilterArgs
): LayerSelectorOption | null => {
  const layers = findLayerEntries(args);

  const text = layers?.length && getPath(layers[0], COMMON_PATHS.TEXT_IN_LAYER);
  if (!text) {
    return null;
  }
  const option = buildBaseOption(
    args,
    buildRenderText(
      text,
      args.layerName,
      normalizeAssetId(args.assetId, args.fallbackAssetId ?? MAIN_ASSET)
    )
  );
  return option;
};

export const buildParagraphTextLayerOption = (
  args: FilterArgs
): LayerSelectorOption | null => {
  const layers = findLayerEntries(args);
  if (!layers?.length) return null;
  const text = getPath(layers[0], COMMON_PATHS.TEXT_IN_LAYER);
  const isParagraph = getPath(layers[0], COMMON_PATHS.PARAGRAPH_IN_LAYER);
  if (!text || !isParagraph) {
    return null;
  }
  const option = buildBaseOption(
    args,
    buildRenderText(
      text,
      args.layerName,
      normalizeAssetId(args.assetId, args.fallbackAssetId ?? MAIN_ASSET)
    )
  );
  return option;
};

export const buildColorLayerOption = (
  args: FilterArgs
): LayerSelectorOption | null => {
  const list: LayerSelectorOption[] = [];
  const fallbackAssetId = args.fallbackAssetId ?? MAIN_ASSET;
  const normalizedAssetId = normalizeAssetId(args.assetId, fallbackAssetId);
  getLayersWithColorArray(
    list,
    args.layerName,
    normalizedAssetId,
    args.layerUid,
    args.additionals,
    args.json
  );
  if (!list.length) {
    return null;
  }
  return list[0];
};

export const isTextLayerSelectable = (args: FilterArgs): boolean =>
  Boolean(buildTextLayerOption(args));

export const isParagraphLayerSelectable = (args: FilterArgs): boolean =>
  Boolean(buildParagraphTextLayerOption(args));

export const isColorLayerSelectable = (args: FilterArgs): boolean =>
  Boolean(buildColorLayerOption(args));
